import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Gift, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const STORAGE_KEY = 'welcomePopupDismissed';
const CODE_RECEIVED_KEY = 'welcomeCodeReceived';

const WelcomePopup = () => {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [visible, setVisible] = useState(false);
  const [savedCode, setSavedCode] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user) return; // Don't show to logged-in users
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) return; // Hide on admin
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    // Suppress popup once the visitor has already claimed a code on this device.
    // We only store a boolean flag — the actual code is delivered server-side via email.
    if (localStorage.getItem(CODE_RECEIVED_KEY) === '1') return;
    (async () => {
      try {
        const res = await api.get('/welcome-popup');
        if (!res.data?.enabled) return;
        setCfg(res.data);
        const delay = Number(res.data.delay_ms) || 4000;
        const t = setTimeout(() => setVisible(true), delay);
        return () => clearTimeout(t);
      } catch (err) {
        console.debug('[welcome-popup] config fetch failed:', err?.message);
      }
    })();
  }, [user]);

  const dismiss = () => {
    sessionStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
  };

  const handleCopy = () => {
    if (!savedCode) return;
    navigator.clipboard.writeText(savedCode);
    setCopied(true);
    toast.success('Rabattkod kopierad!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (!visible || !cfg) return null;

  return (
    <div
      className="fixed bottom-4 left-4 z-50 max-w-xs w-[calc(100%-2rem)] sm:w-80 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden animate-in slide-in-from-bottom-4 duration-500"
      data-testid="welcome-popup"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors z-10"
        aria-label="Stäng"
        data-testid="welcome-popup-close"
      >
        <X className="w-4 h-4" />
      </button>

      {savedCode ? (
        <div className="p-5 pt-7">
          <div className="flex items-center justify-center w-14 h-14 mx-auto bg-green-100 rounded-full mb-3">
            <Gift className="w-7 h-7 text-green-600" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 text-center mb-1">Din rabattkod</h3>
          <p className="text-sm text-slate-600 text-center mb-4">
            Använd koden i kassan för {cfg.discount_percent}% rabatt
          </p>
          <button
            onClick={handleCopy}
            className="w-full flex items-center justify-between gap-2 bg-slate-100 hover:bg-slate-200 transition-colors rounded-lg px-4 py-3 font-mono text-base font-bold text-slate-900"
            data-testid="welcome-popup-copy-code"
          >
            <span>{savedCode}</span>
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-500" />}
          </button>
        </div>
      ) : (
        <div className="p-5 pt-7">
          <div className="flex items-center justify-center w-14 h-14 mx-auto bg-gradient-to-br from-[#2a9d8f] to-[#238b7e] rounded-full mb-3">
            <Gift className="w-7 h-7 text-white" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 text-center mb-1">{cfg.title}</h3>
          <p className="text-sm text-slate-600 text-center mb-4">{cfg.body}</p>
          <Link
            to="/registrera"
            onClick={dismiss}
            className="block w-full text-center bg-[#2a9d8f] hover:bg-[#238b7e] text-white font-semibold rounded-lg py-2.5 transition-colors"
            data-testid="welcome-popup-cta"
          >
            {cfg.button_text}
          </Link>
        </div>
      )}
    </div>
  );
};

// Helper: mark that the visitor has claimed a welcome code (called from Register after signup).
// We store only a boolean flag — the actual code is emailed to the user, never persisted client-side.
export const persistWelcomeCode = (code) => {
  if (code) localStorage.setItem(CODE_RECEIVED_KEY, '1');
};

export default WelcomePopup;
